import * as React from 'react';
import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import MainFeaturedPost from './MainFeaturedPost';
import FeaturedPost from './FeaturedPost';
import { postsApi } from '../services/api';
import Layout from './Layout';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';


// ★ 関数をコンポーネント外に移動（これが一番重要）
const getPaginationItems = (currentPage, totalPages) => {
  const delta = 1;
  const range = new Set();

  // 常に表示するページ: 最初と最後
  range.add(1);
  range.add(totalPages);

  // 現在ページの前後deltaページを追加
  for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
    range.add(i);
  }

  const sortedRange = Array.from(range).sort((a, b) => a - b);
  const rangeWithDots = [];
  let prev = 0;

  for (const num of sortedRange) {
    if (num - prev > 1) {
      rangeWithDots.push('...'); // ← 常に省略記号を挿入
    }
    rangeWithDots.push(num);
    prev = num;
  }

  return rangeWithDots;
};

export default function Blog() {
  const { ym, cat } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featuredPosts, setFeaturedPosts] = useState([]);

  // ★ ページング用の状態
  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 8; //1ページに掲載する記事数

  // ★ URLパラメータが変わった時にページをリセット
  useEffect(() => {
    setCurrentPage(1);
  }, [ym, cat]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        let response;
        if (ym) {
          response = await postsApi.getPostsByMonth(ym);
        } else if (cat) {
          response = await postsApi.getPostsByCategory(cat);
        } else {
          response = await postsApi.getAllPosts();
        }

        const rawPosts = response.data;
        const processedPosts = response.data.map(post => ({
          title: post.title,
          category: post.category,
          date: new Date(post.created_at).toLocaleDateString('ja-JP'),
          description: post.content ? post.content.substring(0, 50).replace(/[#*_`]/g, '') + '...' : '内容なし',
          image: post.image_url,
          imageLabel: 'Post image',
          id: post.id,
          url: `/post/${post.id}`
        }));
        setPosts(processedPosts);
      } catch (err) {
        setError('記事の読み込みに失敗しました');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [ym, cat]);

  // ★ ページング処理
  const totalPages = Math.ceil(posts.length / postsPerPage);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = posts.slice(indexOfFirstPost, indexOfLastPost);

  // ★ 記事を2列に分割
  const splitPosts = () => {
    const leftColumn = [];
    const rightColumn = [];

    currentPosts.forEach((post, index) => {
      if (index % 2 === 0) {
        leftColumn.push(post);
      } else {
        rightColumn.push(post);
      }
    });

    return { leftColumn, rightColumn };
  };

  const { leftColumn, rightColumn } = splitPosts();

  // ★ ページ変更関数
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md')); // md以上かどうか

  return (
    <Layout showSidebar={true}>
      <>
        {loading ? (
          <p>記事を読み込み中...</p>
        ) : error ? (
          <p>{error}</p>
        ) : posts.length === 0 ? (
          <p>記事はありません。</p>
        )
          : (
            <>
              {isMdUp ? (
                // PC/タブレット表示（2列）
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 3,
                  alignItems: 'start',
                  width: '100%',
                }}>
                  {/* 左列 */}
                  <Box sx={{ flex: 1 }}>
                    {leftColumn.map((post) => (
                      <Box key={post.id} sx={{ mb: 4 }}>
                        <FeaturedPost post={post} />
                      </Box>
                    ))}
                  </Box>

                  {/* 右列 */}
                  <Box sx={{ flex: 1 }}>
                    {rightColumn.map((post) => (
                      <Box key={post.id} sx={{ mb: 4 }}>
                        <FeaturedPost post={post} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                // スマホ表示（1列）
                <Box sx={{ width: '100%' }}>
                  {currentPosts.map((post) => (
                    <Box key={post.id} sx={{ mb: 4 }}>
                      <FeaturedPost post={post} />
                    </Box>
                  ))}
                </Box>
              )}

              {/* ★ ページネーションボタン */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  {getPaginationItems(currentPage, totalPages).map((item, index) =>
                    item === '...' ? (
                      <span key={`ellipsis-${index}`} style={{ margin: '0 5px', color: 'gray', display: 'flex', alignItems: 'center' }}>…</span>
                    ) : (
                      <button
                        key={`page-${item}`}
                        onClick={() => paginate(item)}
                        style={{
                          margin: '0 5px',
                          fontWeight: currentPage === item ? 'bold' : 'normal',
                          backgroundColor: currentPage === item ? '#f0f0f0' : 'transparent',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          minWidth: '32px'
                        }}
                      >
                        {item}
                      </button>
                    )
                  )}
                </Box>
              )}
            </>
          )}
      </>
    </Layout>
  );
}